import { Suspense, lazy, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import AdminCollectionPage from './pages/admin/AdminCollectionPage.jsx';
import { AdminLayout } from './components/layout/AdminLayout.jsx';
import { ProtectedRoute } from './components/layout/ProtectedRoute.jsx';
import { RequirePermission } from './components/layout/RequirePermission.jsx';
import { PublicLayout } from './components/layout/PublicLayout.jsx';
import { ConfirmProvider } from './components/ui/ConfirmDialog.jsx';
import { ErrorBoundary } from './components/ui/ErrorBoundary.jsx';
import { LoadingScreen } from './components/ui/LoadingScreen.jsx';
import { ToastViewport } from './components/ui/Toast.jsx';

const Home = lazy(() => import('./pages/Home.jsx'));
const About = lazy(() => import('./pages/About.jsx'));
const Services = lazy(() => import('./pages/Services.jsx'));
const ServiceDetail = lazy(() => import('./pages/ServiceDetail.jsx'));
const Projects = lazy(() => import('./pages/Projects.jsx'));
const ProjectDetail = lazy(() => import('./pages/ProjectDetail.jsx'));
const Contact = lazy(() => import('./pages/Contact.jsx'));
const Login = lazy(() => import('./pages/admin/Login.jsx'));
const Dashboard = lazy(() => import('./pages/admin/Dashboard.jsx'));
const UserManagement = lazy(() => import('./pages/admin/UserManagement.jsx'));
const MediaLibrary = lazy(() => import('./pages/admin/MediaLibrary.jsx'));
const ContactRequests = lazy(() => import('./pages/admin/ContactRequests.jsx'));
const ContactRequestDetail = lazy(() => import('./pages/admin/ContactRequestDetail.jsx'));
const HomepageManager = lazy(() => import('./pages/admin/HomepageManager.jsx'));

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [pathname]);

  return null;
}

export default function App() {
  return (
    <ErrorBoundary>
      <ConfirmProvider>
        <ScrollToTop />
        <ToastViewport />
        <Suspense fallback={<LoadingScreen />}>
          <Routes>
            <Route element={<PublicLayout />}>
              <Route index element={<Home />} />
              <Route path="about" element={<About />} />
              <Route path="services" element={<Services />} />
              <Route path="services/:slug" element={<ServiceDetail />} />
              <Route path="projects" element={<Projects />} />
              <Route path="projects/:slug" element={<ProjectDetail />} />
              <Route path="contact" element={<Contact />} />
            </Route>

            <Route path="admin/login" element={<Login />} />
            <Route element={<ProtectedRoute />}>
              <Route path="admin" element={<AdminLayout />}>
                <Route index element={<Dashboard />} />
                <Route element={<RequirePermission permission="canEditContent" />}>
                  <Route path="homepage" element={<HomepageManager />} />
                  <Route path="services" element={<AdminCollectionPage resource="services" />} />
                  <Route path="projects" element={<AdminCollectionPage resource="projects" />} />
                  <Route path="testimonials" element={<AdminCollectionPage resource="testimonials" />} />
                  <Route path="media" element={<MediaLibrary />} />
                </Route>
                <Route element={<RequirePermission permission="canViewContacts" />}>
                  <Route path="contacts" element={<ContactRequests />} />
                  <Route path="contacts/:id" element={<ContactRequestDetail />} />
                </Route>
                <Route element={<RequirePermission permission="canManageUsers" />}>
                  <Route path="users" element={<UserManagement />} />
                </Route>
                <Route path="*" element={<Navigate to="/admin" replace />} />
              </Route>
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </ConfirmProvider>
    </ErrorBoundary>
  );
}
